import AdsClickOutlinedIcon from '@mui/icons-material/AdsClickOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { Box, styled, Tooltip, useTheme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import { Recommendation, SubBlockType } from 'common/@types/recommendations';
import { getContentType } from 'common/utils/contentTypeParser';
import { useSnackbar } from 'notistack';
import { isRecCompleted } from './AccordionListingContainer';
import CustomContentTypeIcon from './CustomContentTypeIcon';
interface Props {
  index: number;
  recommendation: Recommendation;
  expanded: number;
  handleChange: (id: number) => void;
  type: 'learn' | 'test';
  renderUrlContent: (
    row: Recommendation,
    field: 'suggested_content' | 'url'
  ) => React.JSX.Element | undefined;
  blocked: boolean;
  hideGrade?: boolean;
}

const HeaderInfoStyleDiv = styled('div')(({ theme }) => ({
  minWidth: '160px',
  display: 'flex',
  flexDirection: 'row',
  textAlign: 'start',
  gap: '10px',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  fontWeight: 600,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    opacity: 0.6,
  },
  [theme.breakpoints.down('xl')]: {
    minWidth: '140px',
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: '11px',
    minWidth: '130px',
  },
}));

const DescriptionContainerDiv = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '10px',
  fontSize: '14px',
  [theme.breakpoints.down('xl')]: {
    fontSize: '13px',
  },
}));

const LessonTestAccordian = ({
  index,
  recommendation,
  expanded,
  handleChange,
  type,
  renderUrlContent,
  blocked,
  hideGrade,
}: Props) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  return (
    <Accordion
      TransitionProps={{
        unmountOnExit: true,
      }}
      style={{
        backgroundColor: expanded === recommendation.recommendation_id ? '#dceff5bd' : '#ebf6fabd',
        padding: '10px',
        marginBottom: '0px',
        marginTop: '0px',
      }}
      disabled={blocked}
      onClick={() => {
        if (!blocked) return;
        enqueueSnackbar('You must complete the previous lesson to proceed', {
          variant: 'info',
        });
      }}
      expanded={expanded === recommendation.recommendation_id}
      onChange={(e, expanded) => handleChange(recommendation.recommendation_id)}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="panel1-content"
        id="panel1-header"
      >
        <Typography
          sx={{
            textDecoration: isRecCompleted(recommendation) ? 'line-through' : 'none',
            fontWeight: 600,
            fontSize: '15px',
            maxWidth: '50%',
            '&:hover': {
              opacity: 0.6,
            },
            [theme.breakpoints.down('xl')]: {
              fontSize: '13px',
              maxWidth: '40%',
            },
            [theme.breakpoints.down('lg')]: {
              fontSize: '12px',
              maxWidth: '40%',
            },
          }}
        >
          {type === 'learn'
            ? recommendation.name?.toLowerCase().includes('quiz')
              ? 'Quiz '
              : `Lesson `
            : `Test `}
          {index + 1} - {recommendation.name}
        </Typography>
        <div
          style={{
            display: 'grid',
            maxWidth: '50%',
            gridTemplateColumns: hideGrade ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: 'auto',
            marginRight: '10px',
          }}
        >
          <Tooltip title="Smart Score on this skill">
            <HeaderInfoStyleDiv>
              <AdsClickOutlinedIcon />
              <div>{recommendation.smart_score}</div>
            </HeaderInfoStyleDiv>
          </Tooltip>
          {!hideGrade && (
            <Tooltip title="Grade Level">
              <HeaderInfoStyleDiv>
                <SchoolOutlinedIcon />
                <div>{recommendation.grade ? recommendation.grade : 'N/A'}</div>
              </HeaderInfoStyleDiv>
            </Tooltip>
          )}
          <Tooltip title="Recommendation Source">
            <HeaderInfoStyleDiv>
              <LinkOutlinedIcon />
              <div>
                {recommendation.recommendation_source
                  ? recommendation.recommendation_source
                  : 'N/A'}
              </div>
            </HeaderInfoStyleDiv>
          </Tooltip>
        </div>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            fontSize: '14px',
          }}
        >
          {recommendation?.suggested_content
            ? renderUrlContent(recommendation, 'suggested_content')
            : null}
          <DescriptionContainerDiv>
            <CustomContentTypeIcon type={getContentType(recommendation.url)} />
            <span style={{ fontWeight: 600 }}>
              {type === SubBlockType.LEARN
                ? recommendation.name?.toLowerCase().includes('quiz')
                  ? 'Quiz'
                  : 'Lesson'
                : 'Test'}{' '}
              -{' '}
            </span>
            {renderUrlContent(recommendation, 'url')}{' '}
          </DescriptionContainerDiv>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default LessonTestAccordian;
